import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, ArrowLeft } from 'lucide-react'; // Pastikan lucide-react terinstall

export default function HelpdeskAdmin() {
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filter & Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // State untuk Chat/Detail
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [pesanBalasan, setPesanBalasan] = useState('');

  const token = localStorage.getItem('token');
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchTickets(page);
  }, [page, filterStatus]); // Akan memanggil fetchTickets ketika page atau status berubah

  const fetchTickets = async (currentPage) => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/helpdesk?page=${currentPage}&limit=10&status=${filterStatus}&search=${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Gagal mengambil data tiket");
      const data = await res.json();
      setTickets(data.data);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      console.error(err);
    }
  };

  // Fungsi khusus saat tombol cari diklik / tekan enter
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1); // Reset ke halaman 1 saat mencari
    fetchTickets(1);
  };

  const handleDownload = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/admin/helpdesk/export?status=${filterStatus}&search=${searchQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.length === 0) {
        alert("Tidak ada data untuk didownload dengan filter saat ini.");
        return;
      }

      // Convert JSON ke CSV (Bisa dibuka di Excel)
      const headers = Object.keys(data[0]).join(',');
      const rows = data.map(row => 
        Object.values(row).map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')
      ).join('\n');
      const csvContent = `${headers}\n${rows}`;
      
      // Trigger Download HTML5
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `Data_Helpdesk_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Gagal mendownload data", err);
      alert("Terjadi kesalahan saat mengekspor data.");
    }
  };

  const updateStatus = async (id, newStatus) => {
    if (!window.confirm(`Ubah status tiket ini menjadi ${newStatus}?`)) return;
    try {
      await fetch(`${BASE_URL}/api/admin/helpdesk/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      if (selectedTicket && selectedTicket.id === id) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
      fetchTickets(page);
    } catch (err) {
      console.error(err);
    }
  };

  const openTicketDetail = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const res = await fetch(`${BASE_URL}/api/helpdesk/${ticket.id}/replies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Gagal mengambil percakapan");
      const data = await res.json();
      setReplies(data);
    } catch (err) {
      console.error(err);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BASE_URL}/api/helpdesk/${selectedTicket.id}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pesan: pesanBalasan })
      });
      if (res.ok) {
        setPesanBalasan('');
        openTicketDetail(selectedTicket); 
        fetchTickets(page); 
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto animate-fade-in-up">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Monitoring Helpdesk</h2>
          <p className="text-sm text-slate-500">Pantau dan kelola laporan kendala dari pengguna.</p>
        </div>
      </div>

      {!selectedTicket ? (
        <>
          {/* TOOLBAR: Search, Filter, dan Download */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">
            
            <form onSubmit={handleSearchSubmit} className="flex w-full md:max-w-md relative">
              <input 
                type="text" 
                placeholder="Cari nama, NIP, atau judul..." 
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
              <button type="submit" className="hidden">Cari</button>
            </form>

            <div className="flex w-full md:w-auto gap-3 items-center">
              <div className="relative flex-1 md:flex-none">
                <select 
                  className="w-full md:w-auto appearance-none pl-10 pr-8 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 font-medium cursor-pointer"
                  value={filterStatus}
                  onChange={(e) => {
                    setPage(1);
                    setFilterStatus(e.target.value);
                  }}
                >
                  <option value="">Semua Status</option>
                  <option value="Open">Open (Baru)</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
                <Filter className="absolute left-3 top-2.5 text-slate-400" size={18} />
              </div>

              <button 
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm whitespace-nowrap"
              >
                <Download size={16} /> <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 font-semibold">Tgl</th>
                    <th className="p-4 font-semibold">Pelapor</th>
                    <th className="p-4 font-semibold">Judul</th>
                    <th className="p-4 font-semibold">Status</th>
                    <th className="p-4 font-semibold text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tickets.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-500 font-medium">Data tidak ditemukan.</td>
                    </tr>
                  ) : (
                    tickets.map(t => (
                      <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4 text-slate-600">{new Date(t.created_at).toLocaleDateString('id-ID')}</td>
                        <td className="p-4">
                          <div className="font-bold text-slate-800">{t.nama_pelapor}</div>
                          <div className="text-xs text-slate-500 capitalize">{t.nip} ({t.role})</div>
                        </td>
                        <td className="p-4 text-slate-700 font-medium whitespace-normal min-w-[200px]">{t.judul}</td>
                        <td className="p-4">
                           <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                             t.status === 'Open' ? 'text-rose-700 bg-rose-100 border border-rose-200' : 
                             t.status === 'Resolved' ? 'text-emerald-700 bg-emerald-100 border border-emerald-200' : 
                             t.status === 'Closed' ? 'text-slate-600 bg-slate-200 border border-slate-300' :
                             'text-blue-700 bg-blue-100 border border-blue-200'
                           }`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <button onClick={() => openTicketDetail(t)} className="bg-white border border-slate-300 text-slate-700 px-4 py-1.5 rounded-lg text-sm hover:bg-slate-50 hover:text-blue-600 font-medium transition-colors shadow-sm">
                            Buka
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalPages > 0 && (
            <div className="flex justify-between items-center mt-6">
              <button 
                disabled={page === 1} onClick={() => setPage(page - 1)}
                className="px-4 py-2 bg-white border border-slate-200 shadow-sm rounded-lg disabled:opacity-50 hover:bg-slate-50 text-sm font-medium transition-colors"
              >
                Previous
              </button>
              <span className="text-sm font-medium text-slate-500 bg-white px-4 py-2 border border-slate-200 rounded-lg shadow-sm">
                Halaman {page} dari {totalPages}
              </span>
              <button 
                disabled={page === totalPages} onClick={() => setPage(page + 1)}
                className="px-4 py-2 bg-white border border-slate-200 shadow-sm rounded-lg disabled:opacity-50 hover:bg-slate-50 text-sm font-medium transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        /* Halaman Detail Tiket & Chat untuk Admin (TETAP SAMA SEPERTI SEBELUMNYA) */
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-slate-200">
          <div className="flex flex-col md:flex-row justify-between items-start border-b border-slate-100 pb-6 mb-6 gap-4">
            <div className="flex-1">
              <button onClick={() => setSelectedTicket(null)} className="mb-4 text-slate-400 hover:text-slate-800 text-sm font-medium flex items-center gap-1 transition-colors">
                <ArrowLeft size={16} /> Kembali
              </button>
              <h3 className="text-2xl font-bold text-slate-800">{selectedTicket.judul}</h3>
              <p className="text-slate-500 mt-1 text-sm">Dilaporkan oleh: <span className="font-bold">{selectedTicket.nama_pelapor}</span> ({selectedTicket.role})</p>
              <div className="bg-slate-50 border border-slate-100 p-4 mt-4 rounded-lg text-slate-700 whitespace-pre-wrap">
                {selectedTicket.deskripsi}
              </div>
            </div>
            
            <div className="text-left md:text-right shrink-0 bg-white p-4 rounded-lg border border-slate-200 shadow-sm w-full md:w-auto">
              <span className="text-xs font-bold text-slate-500 block mb-2 uppercase tracking-wider">Ubah Status Tiket:</span>
              <select 
                className="border border-slate-300 p-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none w-full md:w-auto"
                value={selectedTicket.status}
                onChange={(e) => updateStatus(selectedTicket.id, e.target.value)}
              >
                <option value="Open">Open</option>
                <option value="In Progress">In Progress</option>
                <option value="Resolved">Resolved</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="space-y-4 mb-6 max-h-96 overflow-y-auto bg-slate-50 p-4 md:p-6 rounded-xl border border-slate-100">
            {replies.length === 0 ? (
              <p className="text-slate-400 text-center font-medium my-10">Belum ada balasan atau tanggapan pada tiket ini.</p>
            ) : null}
            
            {replies.map(r => (
              <div key={r.id} className={`p-4 rounded-xl max-w-[85%] md:max-w-[75%] shadow-sm ${r.role === 'admin' ? 'bg-blue-600 text-white ml-auto rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 mr-auto rounded-tl-none'}`}>
                <p className={`text-xs font-bold mb-1 ${r.role === 'admin' ? 'text-blue-200' : 'text-slate-500'}`}>
                  {r.pengirim} {r.role === 'admin' && '(Admin)'}
                </p>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.pesan}</p>
                <p className={`text-[10px] text-right mt-2 ${r.role === 'admin' ? 'text-blue-300' : 'text-slate-400'}`}>
                  {new Date(r.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            ))}
          </div>

          <form onSubmit={sendReply} className="flex gap-3">
            <input 
              type="text" placeholder="Ketik balasan untuk pelapor..." required
              className="flex-1 border border-slate-300 p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 bg-slate-50"
              value={pesanBalasan} onChange={(e) => setPesanBalasan(e.target.value)}
            />
            <button type="submit" className="bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-md">
              Kirim
            </button>
          </form>
        </div>
      )}
    </div>
  );
}